import { useCallback, useEffect, useRef, useState } from 'react';
import { useNonReactiveStore } from '../store/calendar-grid.store';

type ScrollDirection = 'left' | 'right';
type ViewportSize = { height: number; width: number };

export const useScrollContainerRef = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [{ height: visibleHeight, width: visibleWidth }, setViewportSize] = useState<ViewportSize>({
    height: 0,
    width: 0,
  });
  const store = useNonReactiveStore();

  const handleScroll = useCallback((direction: ScrollDirection) => {
    const element = containerRef.current;

    if (!element) return;

    const scrollAmount = Math.max(element.clientWidth * 0.8, 1);
    const left = direction === 'left' ? -scrollAmount : scrollAmount;

    element.scrollBy({ left, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    let scrollFrameId = 0;

    if (!element) return;

    const syncViewportSize = () => {
      const nextViewportSize = {
        height: element.clientHeight,
        width: element.clientWidth,
      };

      setViewportSize((currentViewportSize) =>
        currentViewportSize.width === nextViewportSize.width && currentViewportSize.height === nextViewportSize.height
          ? currentViewportSize
          : nextViewportSize
      );
    };

    const syncScrollState = () => {
      scrollFrameId = 0;

      const maxScrollLeft = Math.max(element.scrollWidth - element.clientWidth, 0);
      const canScrollLeft = element.scrollLeft > 0;
      const canScrollRight = maxScrollLeft - element.scrollLeft > 1;
      const { canScrollLeft: previousCanScrollLeft, canScrollRight: previousCanScrollRight } = store.getState();

      if (previousCanScrollLeft === canScrollLeft && previousCanScrollRight === canScrollRight) {
        return;
      }

      store.setState({ canScrollLeft, canScrollRight });
    };

    const scheduleSyncScrollState = () => {
      if (scrollFrameId !== 0) return;
      scrollFrameId = window.requestAnimationFrame(syncScrollState);
    };

    const handleResize = () => {
      syncViewportSize();
      scheduleSyncScrollState();
    };

    const resizeObserver = new ResizeObserver(handleResize);

    syncViewportSize();
    scheduleSyncScrollState();
    resizeObserver.observe(element);
    element.addEventListener('scroll', scheduleSyncScrollState, { passive: true });

    return () => {
      if (scrollFrameId !== 0) {
        window.cancelAnimationFrame(scrollFrameId);
      }

      resizeObserver.disconnect();
      element.removeEventListener('scroll', scheduleSyncScrollState);
    };
  }, [store]);

  return {
    containerRef,
    handleScroll,
    visibleHeight,
    visibleWidth,
  };
};
